import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TrackerLayout from '../components/TrackerLayout';
import { ClassLogEditModal } from '../components/ClassLogEdit';
import { useTracker } from '../context/TrackerContext';
import { getRecentClasses, requestClassLogDelete, requestClassLogEdit } from '../lib/api';
import type { ClassLogEditChanges, RecentClassRow } from '../lib/api';

// Bulk class-log editor for mentors and Student Coordinators. Cells are edited
// in place (or several rows at once via "apply to selected"), and classes can
// be marked for deletion; every changed class is sent as its own request, and
// nothing in the log changes until an admin approves it
// (Admin → Log Edit Requests).

const FIELDS = ['date', 'attendance', 'startTime', 'endTime', 'topic', 'portion', 'remark'] as const;
type Field = typeof FIELDS[number];
type Draft = Partial<Record<Field, string>>;   // only the fields the user changed

const FIELD_LABEL: Record<Field, string> = {
  date: 'Date', attendance: 'Attendance', startTime: 'Start', endTime: 'End',
  topic: 'Topic / Task', portion: 'Portion', remark: 'Remark',
};

const RANGES = [
  { key: '7', label: 'Last 7 days' },
  { key: '30', label: 'Last 30 days' },
  { key: 'all', label: 'All' },
  { key: 'custom', label: 'Custom' },
] as const;
type Range = typeof RANGES[number]['key'];

type EditFilter = 'all' | 'unsent' | 'none' | 'Pending' | 'Approved' | 'Rejected' | 'delete';
type SortKey = 'newest' | 'oldest' | 'student' | 'logged';

const DEFAULT_RANGE: Range = '30';

const valueOf = (row: RecentClassRow, f: Field) => String(row[f] ?? '');

// Locked cells are plain text, so show dates and times the way the inputs do.
function display(f: Field, v: string) {
  if (!v) return '';
  if (f === 'date') return new Date(`${v}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  if (f === 'startTime' || f === 'endTime') {
    const [h, m] = v.split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
  }
  return v;
}

const COL_WIDTH: Record<Field, string> = {
  date: 'min-w-[130px]', attendance: 'min-w-[100px]', startTime: 'min-w-[105px]', endTime: 'min-w-[105px]',
  topic: 'min-w-[190px]', portion: 'min-w-[140px]', remark: 'min-w-[160px]',
};

function daysAgo(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

// Filter dropdowns light up purple while they're narrowing the list.
const selectCls = (active: boolean) =>
  `px-3 py-2 border-2 rounded-xl text-xs font-semibold focus:outline-none focus:border-purple-400 ${
    active ? 'border-purple-300 bg-purple-50 text-purple-800' : 'border-gray-200 bg-white text-gray-600'}`;

// One editable cell. Changed cells turn gold so the user can see what will be sent.
const Cell: React.FC<{ field: Field; value: string; original: string; changed: boolean; onChange: (v: string) => void }> =
  ({ field, value, original, changed, onChange }) => {
    const cls = `w-full rounded-lg px-2 py-1.5 text-xs border transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 ${
      changed ? 'bg-amber-50 border-amber-300 font-semibold text-amber-900' : 'bg-transparent border-transparent hover:border-gray-200 text-gray-700'}`;
    const title = changed ? `Was: ${display(field, original) || '—'}` : undefined;
    if (field === 'attendance') {
      const options = ['Present', 'Absent', ...(original && !['Present', 'Absent'].includes(original) ? [original] : [])];
      return (
        <select value={value} onChange={e => onChange(e.target.value)} className={cls} title={title}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    const type = field === 'date' ? 'date' : field === 'startTime' || field === 'endTime' ? 'time' : 'text';
    return <input type={type} value={value} onChange={e => onChange(e.target.value)} className={cls} title={title} placeholder="—" />;
  };

// Read-only cell: a class waiting on admin (shows current → requested) or one
// being deleted (struck through).
const LockedCell: React.FC<{ field: Field; current: string; requested?: string; strike?: boolean }> =
  ({ field, current, requested, strike }) => (
    <div className="px-2 py-1.5 text-xs leading-snug">
      {requested !== undefined ? (
        <>
          <span className="block text-gray-400 line-through">{display(field, current) || '—'}</span>
          <span className="block font-semibold text-purple-700">{display(field, requested) || '—'}</span>
        </>
      ) : <span className={strike ? 'text-red-400 line-through' : 'text-gray-500'}>{display(field, current) || '—'}</span>}
    </div>
  );

const StatTile: React.FC<{ label: string; value: number; color: string; className?: string }> = ({ label, value, color, className = '' }) => (
  <div className={`rounded-2xl px-4 py-3 border border-white/10 ${className}`} style={{ background: 'rgba(255,255,255,0.07)' }}>
    <p className="text-2xl font-extrabold" style={{ color }}>{value}</p>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-white/50 mt-0.5">{label}</p>
  </div>
);

const EditClassLogs: React.FC = () => {
  const { session } = useTracker();
  const isCoordinator = session?.kind === 'coordinator';
  const requesterId = session?.kind === 'mentor' || session?.kind === 'coordinator' ? session.mentorId : '';
  const myName = session?.kind === 'mentor' || session?.kind === 'coordinator' ? session.mentorName : '';

  const [rows, setRows] = useState<RecentClassRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [toDelete, setToDelete] = useState<Set<string>>(new Set());
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [revising, setRevising] = useState<RecentClassRow | null>(null);

  const [search, setSearch] = useState('');
  const [student, setStudent] = useState('all');
  const [mentor, setMentor] = useState('all');
  const [range, setRange] = useState<Range>(DEFAULT_RANGE);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [attendance, setAttendance] = useState('all');
  const [task, setTask] = useState('all');
  const [editFilter, setEditFilter] = useState<EditFilter>('all');
  const [sort, setSort] = useState<SortKey>('newest');

  const [bulkField, setBulkField] = useState<Field>('date');
  const [bulkValue, setBulkValue] = useState('');

  // Coordinators look after every student; mentors see the classes they took or own.
  const load = useCallback(async () => {
    if (!requesterId) return;
    setLoading(true);
    try {
      const res = await getRecentClasses(isCoordinator ? '' : requesterId, isCoordinator ? 300 : 150);
      setRows(res.rows);
    } catch (err: any) { toast.error('Failed to load classes: ' + err.message); }
    finally { setLoading(false); }
  }, [requesterId, isCoordinator]);

  useEffect(() => { load(); }, [load]);

  const isLocked = (row: RecentClassRow) => row.editRequest?.status === 'Pending';
  const isEditable = (row: RecentClassRow) => !isLocked(row) && !toDelete.has(row.logRowIndex);

  const setCell = (row: RecentClassRow, f: Field, v: string) => {
    const id = row.logRowIndex;
    setDrafts(prev => {
      const next: Draft = { ...(prev[id] ?? {}) };
      if (v === valueOf(row, f)) delete next[f]; else next[f] = v;
      const out = { ...prev };
      if (Object.keys(next).length) out[id] = next; else delete out[id];
      return out;
    });
    setRowErrors(e => { const { [id]: _, ...rest } = e; return rest; });
  };

  // Marking a class for deletion drops any unsent edits to it — they'd be moot.
  const markDelete = (ids: string[]) => {
    setToDelete(s => { const n = new Set(s); ids.forEach(id => n.add(id)); return n; });
    setDrafts(d => { const out = { ...d }; ids.forEach(id => delete out[id]); return out; });
    setRowErrors(e => { const out = { ...e }; ids.forEach(id => delete out[id]); return out; });
    setSelected(s => { const n = new Set(s); ids.forEach(id => n.delete(id)); return n; });
  };
  const unmarkDelete = (id: string) => setToDelete(s => { const n = new Set(s); n.delete(id); return n; });
  const undoRow = (id: string) => setDrafts(d => { const { [id]: _, ...rest } = d; return rest; });

  const students = useMemo(
    () => Array.from(new Map(rows.map(r => [r.studentId, r.studentName])).entries()).sort((a, b) => a[1].localeCompare(b[1])),
    [rows]);
  const mentors = useMemo(
    () => Array.from(new Set(rows.flatMap(r => [r.takenBy, r.assignedMentor]).filter((n): n is string => !!n))).sort(),
    [rows]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const [lo, hi] = range === 'custom' ? [from, to] : range === 'all' ? ['', ''] : [daysAgo(Number(range)), ''];
    const list = rows.filter(r => {
      const id = r.logRowIndex;
      if (student !== 'all' && r.studentId !== student) return false;
      if (mentor !== 'all' && r.takenBy !== mentor && r.assignedMentor !== mentor) return false;
      if (lo && r.date < lo) return false;
      if (hi && r.date > hi) return false;
      if (attendance === 'Leave' && ['Present', 'Absent'].includes(r.attendance)) return false;
      if (attendance !== 'all' && attendance !== 'Leave' && r.attendance !== attendance) return false;
      if (task !== 'all' && (r.completionStatus || 'N/A') !== task) return false;
      const unsent = !!drafts[id] || toDelete.has(id);
      if (editFilter === 'unsent' && !unsent) return false;
      if (editFilter === 'none' && (r.editRequest || unsent)) return false;
      if (editFilter === 'delete' && !(r.editRequest?.type === 'delete' || toDelete.has(id))) return false;
      if (['Pending', 'Approved', 'Rejected'].includes(editFilter) && r.editRequest?.status !== editFilter) return false;
      if (q && ![r.studentName, r.studentId, r.topic, r.portion, r.remark, r.date].some(v => (v || '').toLowerCase().includes(q))) return false;
      return true;
    });
    // Rows arrive most recently logged first, which is what "logged" keeps.
    if (sort === 'newest') list.sort((a, b) => b.date.localeCompare(a.date));
    else if (sort === 'oldest') list.sort((a, b) => a.date.localeCompare(b.date));
    else if (sort === 'student') list.sort((a, b) => a.studentName.localeCompare(b.studentName) || b.date.localeCompare(a.date));
    return list;
  }, [rows, search, student, mentor, range, from, to, attendance, task, editFilter, sort, drafts, toDelete]);

  const activeFilters = [search.trim() !== '', student !== 'all', mentor !== 'all', range !== DEFAULT_RANGE,
    attendance !== 'all', task !== 'all', editFilter !== 'all'].filter(Boolean).length;

  const clearFilters = () => {
    setSearch(''); setStudent('all'); setMentor('all'); setRange(DEFAULT_RANGE); setFrom(''); setTo('');
    setAttendance('all'); setTask('all'); setEditFilter('all');
  };

  const stats = useMemo(() => ({
    pending:  rows.filter(r => r.editRequest?.status === 'Pending').length,
    approved: rows.filter(r => r.editRequest?.status === 'Approved').length,
    rejected: rows.filter(r => r.editRequest?.status === 'Rejected').length,
  }), [rows]);

  const deleteIds = Array.from(toDelete);
  const editIds = Object.keys(drafts).filter(id => !toDelete.has(id));
  const unsentTotal = deleteIds.length + editIds.length;
  const changeCount = editIds.reduce((n, id) => n + Object.keys(drafts[id]).length, 0);
  const editableVisible = visible.filter(isEditable);
  const allSelected = editableVisible.length > 0 && editableVisible.every(r => selected.has(r.logRowIndex));

  const toggleRow = (id: string) => setSelected(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(editableVisible.map(r => r.logRowIndex)));

  const selectedEditable = () => rows.filter(r => selected.has(r.logRowIndex) && isEditable(r));

  const applyBulk = () => {
    const targets = selectedEditable();
    if (!targets.length) { toast.error('Tick the classes you want to change first'); return; }
    if (bulkField !== 'remark' && bulkField !== 'portion' && !bulkValue) { toast.error(`Enter the new ${FIELD_LABEL[bulkField].toLowerCase()}`); return; }
    targets.forEach(r => setCell(r, bulkField, bulkValue));
    toast.info(`${FIELD_LABEL[bulkField]} set on ${targets.length} class${targets.length > 1 ? 'es' : ''} — review, then send for approval`);
  };

  const bulkDelete = () => {
    const targets = selectedEditable();
    if (!targets.length) { toast.error('Tick the classes you want to delete first'); return; }
    markDelete(targets.map(r => r.logRowIndex));
    toast.info(`${targets.length} class${targets.length > 1 ? 'es' : ''} marked for deletion — add a reason and send for approval`);
  };

  const discardAll = () => { setDrafts({}); setToDelete(new Set()); setRowErrors({}); };

  // Each class becomes its own request (sent one by one), all sharing one reason.
  const handleSend = async () => {
    if (!reason.trim()) { toast.error('Add a reason so admin knows why these classes need changing'); return; }
    if (deleteIds.length && !window.confirm(
      `Ask admin to delete ${deleteIds.length} class log${deleteIds.length > 1 ? 's' : ''}?\n\nThey stay in the log until admin approves.`)) return;
    setSending(true);
    const errors: Record<string, string> = {};
    let sent = 0;
    for (const id of deleteIds) {
      try { await requestClassLogDelete(requesterId, id, reason.trim()); sent++; }
      catch (err: any) { errors[id] = err.message; }
    }
    for (const id of editIds) {
      try { await requestClassLogEdit(requesterId, id, drafts[id] as ClassLogEditChanges, reason.trim()); sent++; }
      catch (err: any) { errors[id] = err.message; }
    }
    setRowErrors(errors);
    setDrafts(prev => Object.fromEntries(Object.entries(prev).filter(([id]) => errors[id])));
    setToDelete(prev => new Set(Array.from(prev).filter(id => errors[id])));
    setSelected(new Set());
    const failed = Object.keys(errors).length;
    if (sent) toast.success(`${sent} request${sent > 1 ? 's' : ''} sent to admin for approval`);
    if (failed) toast.error(`${failed} couldn't be sent — see the rows marked in red`);
    else setReason('');
    setSending(false);
    load();
  };

  if (!session || (session.kind !== 'mentor' && session.kind !== 'coordinator')) {
    return <Navigate to="/tracker/mentor/login" replace />;
  }

  return (
    <TrackerLayout>
      <div className="space-y-5 pb-48">
        {/* ── Hero ── */}
        <div className="relative rounded-3xl overflow-hidden shadow-xl"
          style={{ background: 'linear-gradient(135deg,#1a1430 0%,#2b2240 45%,#4e3a69 100%)' }}>
          <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: '#F0B429' }} />
          <div className="absolute -bottom-24 left-10 w-64 h-64 rounded-full opacity-20 blur-3xl pointer-events-none" style={{ background: '#FF6535' }} />
          <div className="relative px-6 sm:px-8 py-7">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full"
              style={{ background: 'rgba(240,180,41,0.15)', color: '#F0B429' }}>
              ✏️ Class Log Editor
            </span>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white mt-3">Edit Class Logs</h1>
            <p className="text-white/60 text-sm mt-1 max-w-2xl">
              Fix or delete one class or many at once — edit right in the table, then send everything together.
              Nothing changes in the log until admin approves.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6">
              <StatTile label={isCoordinator ? 'All classes' : 'Your classes'} value={rows.length} color="#ffffff" className="col-span-2 sm:col-span-1" />
              <StatTile label="Unsent changes" value={unsentTotal} color="#F0B429" />
              <StatTile label="Awaiting admin" value={stats.pending} color="#fbbf24" />
              <StatTile label="Approved" value={stats.approved} color="#4ade80" />
              <StatTile label="Rejected" value={stats.rejected} color="#f87171" />
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-5 text-xs text-white/50">
              <span><b className="text-white/80">1</b> Edit cells, or tick classes to change / delete in bulk</span>
              <span><b className="text-white/80">2</b> Add one reason</span>
              <span><b className="text-white/80">3</b> Admin approves → log updates</span>
            </div>
          </div>
        </div>

        {/* ── Filters ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
            <div className="relative flex-1 min-w-0">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search student, ID, topic, portion, remark or date…"
                className="w-full pl-9 pr-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400" />
            </div>
            <select value={student} onChange={e => setStudent(e.target.value)} className={selectCls(student !== 'all')}>
              <option value="all">All students</option>
              {students.map(([id, name]) => <option key={id} value={id}>{name} ({id})</option>)}
            </select>
            {mentors.length > 1 && (
              <select value={mentor} onChange={e => setMentor(e.target.value)} className={selectCls(mentor !== 'all')}>
                <option value="all">All mentors</option>
                {mentors.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            )}
            <select value={sort} onChange={e => setSort(e.target.value as SortKey)} className={selectCls(false)}>
              <option value="newest">Sort: newest class first</option>
              <option value="oldest">Sort: oldest class first</option>
              <option value="student">Sort: student A–Z</option>
              <option value="logged">Sort: recently logged</option>
            </select>
            <button onClick={load} className="px-3 py-2 rounded-xl text-xs font-semibold border-2 border-gray-200 text-gray-600 hover:bg-gray-50">↻ Refresh</button>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
              {RANGES.map(r => (
                <button key={r.key} onClick={() => setRange(r.key)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all"
                  style={range === r.key ? { background: '#2b2240', color: '#fff' } : { color: '#6b7280' }}>
                  {r.label}
                </button>
              ))}
            </div>
            {range === 'custom' && (
              <div className="flex items-center gap-1.5">
                <input type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} className={selectCls(!!from)} aria-label="From date" />
                <span className="text-xs text-gray-400">to</span>
                <input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} className={selectCls(!!to)} aria-label="To date" />
              </div>
            )}
            <select value={attendance} onChange={e => setAttendance(e.target.value)} className={selectCls(attendance !== 'all')}>
              <option value="all">Any attendance</option>
              <option value="Present">Present</option>
              <option value="Absent">Absent</option>
              <option value="Leave">Mentor / institute leave</option>
            </select>
            <select value={task} onChange={e => setTask(e.target.value)} className={selectCls(task !== 'all')}>
              <option value="all">Any task status</option>
              <option value="Task Given">Task Given</option>
              <option value="Completed">Completed</option>
              <option value="Not Completed">Not Completed</option>
              <option value="N/A">N/A</option>
            </select>
            <select value={editFilter} onChange={e => setEditFilter(e.target.value as EditFilter)} className={selectCls(editFilter !== 'all')}>
              <option value="all">Any edit status</option>
              <option value="unsent">My unsent changes</option>
              <option value="none">Never edited</option>
              <option value="Pending">Awaiting admin</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="delete">Delete requests</option>
            </select>
            {activeFilters > 0 && (
              <button onClick={clearFilters}
                className="px-3 py-2 rounded-xl text-xs font-bold text-purple-700 hover:bg-purple-50 whitespace-nowrap">
                ✕ Clear filters ({activeFilters})
              </button>
            )}
          </div>
        </div>

        {/* ── Grid ── */}
        {loading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="w-8 h-8 rounded-full border-purple-200 border-t-purple-600 animate-spin" style={{ borderWidth: 3 }} />
            <p className="text-sm text-gray-400">Loading classes…</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-14 text-center">
            <div className="text-5xl mb-3">📭</div>
            <p className="text-gray-700 font-bold">No classes match these filters</p>
            <button onClick={() => { clearFilters(); setRange('all'); }}
              className="text-xs text-purple-600 font-semibold hover:underline mt-2">Show all classes</button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex flex-wrap items-center gap-4 px-4 py-2.5 border-b border-gray-100 text-[11px] text-gray-500">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300" /> Your unsent change</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-100 border border-red-300" /> Marked for deletion</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-purple-100 border border-purple-300" /> Waiting for admin (locked)</span>
              <span className="ml-auto">{visible.length} of {rows.length} classes</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse" style={{ minWidth: 1180 }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg,#2b2240,#3d2f5f)' }}>
                    <th className="px-3 py-3 w-10">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-yellow-400" title="Select all" />
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 tracking-wide sticky left-0 z-10" style={{ background: '#2b2240' }}>Student</th>
                    {FIELDS.map(f => (
                      <th key={f} className="px-2 py-3 text-left text-xs font-semibold text-white/80 tracking-wide whitespace-nowrap">{FIELD_LABEL[f]}</th>
                    ))}
                    {/* Pinned right so the status / actions are visible while scrolling sideways */}
                    <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 tracking-wide sticky right-0 z-10" style={{ background: '#3d2f5f' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(row => {
                    const id = row.logRowIndex;
                    const locked = isLocked(row);
                    const marked = toDelete.has(id);
                    const draft = drafts[id];
                    const req = row.editRequest;
                    const reqKind = req?.type === 'delete' ? 'delete' : 'edit';
                    const rowBg = rowErrors[id] ? '#fef2f2' : marked ? '#fff1f2' : locked ? '#faf5ff' : draft ? '#fffbeb' : '#ffffff';
                    return (
                      <tr key={id} className="border-b border-gray-100 align-top" style={{ background: rowBg }}>
                        <td className="px-3 py-3">
                          <input type="checkbox" disabled={locked || marked} checked={selected.has(id)} onChange={() => toggleRow(id)}
                            className="w-4 h-4 accent-purple-700 disabled:opacity-30" />
                        </td>
                        <td className="px-3 py-2.5 sticky left-0 z-[1] min-w-[170px]" style={{ background: rowBg }}>
                          <p className={`font-semibold text-xs ${marked ? 'text-red-400 line-through' : 'text-gray-800'}`}>{row.studentName}</p>
                          <p className="text-[10px] text-gray-400">{row.studentId} · {row.completionStatus || '—'}</p>
                          {isCoordinator && row.takenBy && <p className="text-[10px] text-gray-400">by {row.takenBy}</p>}
                        </td>
                        {FIELDS.map(f => (
                          <td key={f} className={`px-1 py-1.5 ${COL_WIDTH[f]}`}>
                            {locked || marked
                              ? <LockedCell field={f} current={valueOf(row, f)} requested={locked ? req?.changes?.[f] : undefined}
                                  strike={marked || (locked && reqKind === 'delete')} />
                              : <Cell field={f} original={valueOf(row, f)} value={draft?.[f] ?? valueOf(row, f)}
                                  changed={draft?.[f] !== undefined} onChange={v => setCell(row, f, v)} />}
                          </td>
                        ))}
                        <td className="px-3 py-2.5 min-w-[170px] max-w-[220px] sticky right-0 z-[1]"
                          style={{ background: rowBg, boxShadow: '-8px 0 10px -8px rgba(43,34,64,0.25)' }}>
                          {rowErrors[id] && <p className="text-[11px] font-semibold text-red-600 mb-1">✕ {rowErrors[id]}</p>}
                          {locked ? (
                            <div className="space-y-1">
                              <span className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-md bg-purple-100 text-purple-800">
                                {reqKind === 'delete' ? '🗑 Delete' : '⏳ Edit'} awaiting admin{req?.requestedByName ? ` · ${req.requestedByName}` : ''}
                              </span>
                              {req?.requestedByName === myName && (
                                <button onClick={() => setRevising(row)} className="block text-[11px] font-bold text-purple-600 hover:underline">
                                  ✏️ Revise request
                                </button>
                              )}
                            </div>
                          ) : marked ? (
                            <div className="space-y-1">
                              <span className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-md bg-red-100 text-red-700">🗑 Marked for deletion</span>
                              <button onClick={() => unmarkDelete(id)} className="block text-[11px] font-semibold text-gray-500 hover:text-gray-800">↺ Undo</button>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              {draft ? (
                                <span className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-md bg-amber-100 text-amber-800">
                                  {Object.keys(draft).length} unsent change{Object.keys(draft).length > 1 ? 's' : ''}
                                </span>
                              ) : req?.status === 'Approved' ? (
                                <span className="block text-[11px] font-semibold text-green-700">✓ Last {reqKind} approved</span>
                              ) : req?.status === 'Rejected' ? (
                                <span className="block text-[11px] font-semibold text-red-600">✕ Last {reqKind} rejected{req.reviewNote ? ` — ${req.reviewNote}` : ''}</span>
                              ) : null}
                              <div className="flex gap-3">
                                {draft && <button onClick={() => undoRow(id)} className="text-[11px] font-semibold text-gray-500 hover:text-gray-800">↺ Undo</button>}
                                <button onClick={() => markDelete([id])} className="text-[11px] font-semibold text-red-500 hover:text-red-700">🗑 Delete</button>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Action bars — pinned to the bottom so ticking rows never shifts the table ── */}
        {(selected.size > 0 || unsentTotal > 0) && (
          <div className="sticky bottom-4 z-20 space-y-3">
            {selected.size > 0 && (
              <div className="rounded-2xl p-4 flex flex-col md:flex-row md:items-center gap-3 shadow-2xl border border-white/10"
                style={{ background: 'linear-gradient(135deg,#4e3a69,#2b2240)' }}>
                <p className="text-sm font-bold text-white whitespace-nowrap">{selected.size} selected</p>
                <div className="flex flex-1 flex-col sm:flex-row gap-2">
                  <select value={bulkField} onChange={e => { setBulkField(e.target.value as Field); setBulkValue(''); }}
                    className="px-3 py-2 rounded-xl text-sm bg-white/10 text-white border border-white/20 focus:outline-none">
                    {FIELDS.map(f => <option key={f} value={f} className="text-gray-900">Set {FIELD_LABEL[f]}</option>)}
                  </select>
                  {bulkField === 'attendance' ? (
                    <select value={bulkValue} onChange={e => setBulkValue(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-xl text-sm bg-white text-gray-800 focus:outline-none">
                      <option value="">— choose —</option>
                      <option value="Present">Present</option>
                      <option value="Absent">Absent</option>
                    </select>
                  ) : (
                    <input value={bulkValue} onChange={e => setBulkValue(e.target.value)}
                      type={bulkField === 'date' ? 'date' : bulkField === 'startTime' || bulkField === 'endTime' ? 'time' : 'text'}
                      placeholder={`New ${FIELD_LABEL[bulkField].toLowerCase()}`}
                      className="flex-1 px-3 py-2 rounded-xl text-sm bg-white text-gray-800 focus:outline-none" />
                  )}
                  <button onClick={applyBulk}
                    className="px-4 py-2 rounded-xl text-sm font-bold text-[#2b2240] whitespace-nowrap"
                    style={{ background: 'linear-gradient(135deg,#F0B429,#fcd34d)' }}>
                    Apply to selected
                  </button>
                  <button onClick={bulkDelete}
                    className="px-4 py-2 rounded-xl text-sm font-bold text-white whitespace-nowrap hover:opacity-90"
                    style={{ background: '#dc2626' }}>
                    🗑 Delete selected
                  </button>
                </div>
                <button onClick={() => setSelected(new Set())} className="text-xs text-white/60 hover:text-white whitespace-nowrap">Clear selection</button>
              </div>
            )}
            {unsentTotal > 0 && (
            <div className="rounded-2xl shadow-2xl border border-white/10 p-4 flex flex-col md:flex-row md:items-center gap-3"
              style={{ background: 'linear-gradient(135deg,#1a1430,#2b2240)' }}>
              <div className="whitespace-nowrap">
                <p className="text-white font-bold text-sm">
                  {editIds.length > 0 && `✏️ ${editIds.length} edit${editIds.length > 1 ? 's' : ''}`}
                  {editIds.length > 0 && deleteIds.length > 0 && ' · '}
                  {deleteIds.length > 0 && `🗑 ${deleteIds.length} delete${deleteIds.length > 1 ? 's' : ''}`}
                </p>
                <p className="text-white/50 text-[11px]">
                  {editIds.length > 0 ? `${changeCount} field${changeCount > 1 ? 's' : ''} · ` : ''}each class goes to admin separately
                </p>
              </div>
              <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for these changes (required)"
                className="flex-1 px-3 py-2.5 rounded-xl text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400" />
              <div className="flex gap-2">
                <button onClick={discardAll} disabled={sending}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white/70 border border-white/20 hover:bg-white/10 disabled:opacity-50">
                  Discard
                </button>
                <button onClick={handleSend} disabled={sending}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold text-[#2b2240] whitespace-nowrap disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)' }}>
                  {sending ? 'Sending…' : `Send ${unsentTotal} for Approval`}
                </button>
              </div>
            </div>
            )}
          </div>
        )}
      </div>

      {revising && (
        <ClassLogEditModal row={revising} mentorId={requesterId}
          onClose={() => setRevising(null)}
          onDone={() => { setRevising(null); load(); }} />
      )}
    </TrackerLayout>
  );
};

export default EditClassLogs;
